import { Controller } from "@hotwired/stimulus"

// Lightweight image lightbox with keyboard navigation. Use:
//
//   <div data-controller="lightbox">
//     <a href="/full.jpg" data-action="click->lightbox#open"
//        data-lightbox-caption="My caption"><img src="/thumb.jpg"></a>
//     <a href="/other.jpg" data-action="click->lightbox#open"
//        data-lightbox-caption="Another"><img src="/other-thumb.jpg"></a>
//   </div>
//
// All anchors inside the controller scope are treated as part of the same
// gallery, in DOM order. Keyboard: ←/→ to navigate, Esc to close.
export default class extends Controller {
  connect() {
    this.onKeydown = this.onKeydown.bind(this)
  }

  disconnect() {
    this.close()
  }

  open(event) {
    event.preventDefault()
    const trigger = event.currentTarget
    const items = this.#collectItems()
    const index = items.findIndex((it) => it.el === trigger)
    if (index === -1) return
    this.#mount(items, Math.max(0, index))
  }

  close() {
    if (!this.overlay) return
    document.removeEventListener("keydown", this.onKeydown)
    this.overlay.remove()
    this.overlay = null
    this.items = null
    this.index = 0
    document.body.classList.remove("overflow-hidden")
  }

  prev() {
    if (!this.items) return
    this.index = (this.index - 1 + this.items.length) % this.items.length
    this.#render()
  }

  next() {
    if (!this.items) return
    this.index = (this.index + 1) % this.items.length
    this.#render()
  }

  onKeydown(event) {
    if (event.key === "Escape") this.close()
    if (event.key === "ArrowLeft") this.prev()
    if (event.key === "ArrowRight") this.next()
  }

  #collectItems() {
    return Array.from(this.element.querySelectorAll("[data-action*='lightbox#open']"))
      .map((el) => ({
        el,
        href: el.getAttribute("href"),
        caption: el.dataset.lightboxCaption || ""
      }))
      .filter((it) => it.href)
  }

  #mount(items, index) {
    this.items = items
    this.index = index

    const overlay = document.createElement("div")
    overlay.className = "fixed inset-0 z-[60] flex flex-col bg-black/85 backdrop-blur-sm"
    overlay.innerHTML = `
      <div class="flex items-center justify-between gap-4 px-4 py-3 text-white">
        <p data-role="counter" class="text-xs font-medium tracking-wide opacity-70"></p>
        <button type="button" data-role="close" aria-label="Fechar"
                class="rounded-md border border-white/20 px-3 py-1 text-xs hover:bg-white/10">
          Esc · Fechar
        </button>
      </div>
      <div class="relative flex flex-1 items-center justify-center px-4 pb-2">
        <button type="button" data-role="prev" aria-label="Anterior"
                class="absolute left-3 top-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-black/40 p-2 text-white hover:bg-black/60">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 6l-6 6 6 6"/></svg>
        </button>
        <img data-role="image" alt="" class="max-h-[80vh] max-w-[92vw] rounded-lg object-contain shadow-2xl">
        <button type="button" data-role="next" aria-label="Próximo"
                class="absolute right-3 top-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-black/40 p-2 text-white hover:bg-black/60">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 6l6 6-6 6"/></svg>
        </button>
      </div>
      <p data-role="caption" class="px-4 pb-5 text-center text-sm text-white/80"></p>
    `

    overlay.querySelector("[data-role='close']").addEventListener("click", () => this.close())
    overlay.querySelector("[data-role='prev']").addEventListener("click", () => this.prev())
    overlay.querySelector("[data-role='next']").addEventListener("click", () => this.next())
    overlay.addEventListener("click", (e) => { if (e.target === overlay) this.close() })

    document.body.appendChild(overlay)
    document.body.classList.add("overflow-hidden")
    document.addEventListener("keydown", this.onKeydown)

    this.overlay = overlay
    this.#render()
  }

  #render() {
    if (!this.overlay || !this.items) return
    const item = this.items[this.index]
    const img = this.overlay.querySelector("[data-role='image']")
    const caption = this.overlay.querySelector("[data-role='caption']")
    const counter = this.overlay.querySelector("[data-role='counter']")
    const prev = this.overlay.querySelector("[data-role='prev']")
    const next = this.overlay.querySelector("[data-role='next']")

    img.src = item.href
    img.alt = item.caption || ""
    caption.textContent = item.caption || ""
    counter.textContent = `${this.index + 1} / ${this.items.length}`

    const single = this.items.length <= 1
    prev.classList.toggle("hidden", single)
    next.classList.toggle("hidden", single)
  }
}
